import { reactive } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { usePermission } from '@/composables/permission.js'
import { useFeature } from '@/composables/feature.js'

const menuChildCollapseState = reactive({})

const eventAppChild = (name, label, page, extra = {}) => ({
  name: `EventApp/${name}`,
  label,
  route: 'eventApp.embed',
  extraParams: { page },
  ...extra,
})

/**
 * The gate of the event's „Programm" pages (Inhalte): every product that reads them, the Airstage
 * landing pages included (Airstage/Guest/LiveEventProgram.php; Arnold, 22 Oct 2026).
 */
const PROGRAMME = {
  anyFeature: ['pwa-builder', 'scan-my-lead', 'airstage'],
  group: 'programme',
}

/** The heading keys of a section's page groups (a child's `group`). */
const CHILD_GROUP_HEADINGS = {
  libraries: 'sidebar.group.libraries',
  programme: 'sidebar.group.programme',
}

/**
 * The heading to show before the child at `index`, or null: a group's first visible child gets it,
 * and only while more than one group is visible (a lone group needs no heading).
 *
 * @param {Array<{group?: string}>} children the visible children, in menu order
 * @param {number} index
 * @returns {string|null} a translation key
 */
export const childGroupHeading = (children, index) => {
  const group = children[index]?.group

  if (!group || new Set(children.map((child) => child.group)).size < 2) {
    return null
  }

  return children[index - 1]?.group === group
    ? null
    : (CHILD_GROUP_HEADINGS[group] ?? null)
}

const scanMyLeadChild = (name, label, screen, extra = {}) => ({
  name: `ScanMyLead/${name}`,
  label,
  route: 'scanMyLead.screen',
  extraParams: { screen },
  feature: 'scan-my-lead',
  ...extra,
})

/**
 * Whether a child row is the page that is currently open.
 *
 * The component name used to be the whole test, which breaks as soon as several
 * children share ONE Inertia component: every Connect & Meet screen is served
 * by `EventApp/Meetings`, so both rows lit up at once. The page slug the child
 * links to breaks that tie.
 *
 * The slug is consulted only when BOTH sides state one. A child that names no
 * page, and a host that renders no `page` prop (every Filament-embedded child),
 * therefore behave exactly as before: component name only.
 */
export const isMenuChildActive = (page, childMenu) => {
  if (page?.component !== childMenu?.name) {
    return false
  }

  const openPage = page?.props?.page
  const childPage = childMenu?.extraParams?.page

  if (typeof openPage !== 'string' || typeof childPage !== 'string') {
    return true
  }

  return openPage === childPage
}

export const useMenuChildCollapse = (label) => ({
  isOpen: () => menuChildCollapseState[label] === true,
  toggle: () => {
    menuChildCollapseState[label] = !menuChildCollapseState[label]
  },
})

/**
 * `navKey`: the entry may be hidden by the customer (Marketplace concept §7); the value is the
 * server's enum MenuEntry (Module domain). useVisibleMenuElements (navigationPreferences.js) leaves
 * hidden entries out of the sidebars and the mobile menu.
 */
export const useMenus = [
  {
    name: 'Events/Cockpit',
    label: 'cockpit',
    icon: ['fas', 'chart-pie'],
    route: 'events.cockpit',
    params: [],
    permission: 'event:statistic:view',
    activeRoutes: [],
    child: [],
    buttons: [],
    selector: 'header_menu_home',
  },
  {
    name: 'Events/Reports/Index',
    label: 'custom_reports',
    icon: ['fas', 'chart-simple'],
    route: 'events.reports.index',
    params: [],
    feature: 'custom_reports',
    permission: 'event:statistic:view',
    activeRoutes: [],
    child: [],
    buttons: [],
    selector: 'header_menu_custom_reports',
  },
  {
    name: 'Bookings/List',
    label: 'guests',
    icon: ['fas', 'users'],
    route: 'bookings.index',
    params: ['event.id'],
    dropdown: true,
    permission: 'guest:list',
    activeRoutes: [],
    child: [
      {
        name: 'Bookings/List',
        label: 'guestlist',
        route: 'bookings.index',
        permission: 'guest:list',
      },
      {
        name: 'GuestGroups/List',
        label: 'guest_groups',
        icon: ['fas', 'user-group'],
        route: 'guest-groups.index',
        permission: 'guestGroup:list',
        selector: 'header_menu_guestgroups',
      },
      {
        name: 'SubEvents/List',
        label: 'sub_events',
        icon: ['fal', 'calendar-days'],
        route: 'sub-events.index',
        permission: 'subEvent:list',
        feature: 'sub-events',
        selector: 'header_menu_subevents',
      },
      {
        name: 'Bookings/Checkins/List',
        label: 'checkin_history',
        icon: ['fa', 'face-viewfinder'],
        route: 'bookings.checkins.list',
        permission: 'guest:checkin:list',
        selector: 'header_menu_checkin_history',
      },
      {
        name: 'Events/Show/Checkin/Cockpit',
        label: 'checkin_cockpit',
        icon: ['fas', 'qrcode'],
        route: 'events.checkin.cockpit',
        permission: 'checkinCockpit:view',
        feature: 'checkin-cockpit',
        selector: 'header_menu_checkin_cockpit',
      },
      {
        name: 'Bookings/GuestManagers',
        label: 'guestManagersList',
        icon: ['fal', 'users'],
        route: 'guest-managers.index',
        constantFlag: 'allowGuestManagers',
        selector: 'header_menu_guest_managers',
      },
    ],
    buttons: [],
    selector: 'header_menu_guestlist',
  },
  {
    // Event Planner (SN-021) right after Gäste (Arnold, 22 Oct 2026).
    name: 'Planner/Overview',
    label: 'planner',
    navKey: 'planner',
    icon: ['fas', 'clipboard-list'],
    // The design-system sidebar shows the DS line icon (no illustration for the planner yet).
    dsIcon: 'clipboard',
    route: 'planner.overview',
    params: ['event.id'],
    plannerEvent: true,
    // While the company may start the beta, the parent leads to the start screen.
    plannerStart: true,
    // Package 29 (B3): opened from the sidebar, after the trial or a booking Planung shows the
    // page „Testphase beendet“ first (sidebar links only, never the section tabs).
    entryParams: { from: 'menu' },
    dropdown: true,
    child: [
      {
        name: 'Planner/Overview',
        label: 'planner_overview',
        route: 'planner.overview',
        params: ['event.id'],
        plannerEvent: true,
        entryParams: { from: 'menu' },
      },
      {
        name: 'Planner/Tasks',
        label: 'planner_tasks',
        route: 'planner.tasks',
        params: ['event.id'],
        plannerEvent: true,
      },
      {
        name: 'Planner/Budget',
        label: 'planner_budget',
        route: 'planner.budget',
        params: ['event.id'],
        plannerEvent: true,
      },
      {
        name: 'Planner/Vendors',
        label: 'planner_vendors',
        route: 'planner.vendors',
        params: ['event.id'],
        plannerEvent: true,
      },
      {
        name: 'Planner/Sponsors',
        label: 'planner_sponsors',
        route: 'planner.sponsors',
        params: ['event.id'],
        plannerEvent: true,
      },
      {
        name: 'Planner/Costs',
        label: 'planner_costs',
        route: 'planner.costs',
        params: ['event.id'],
        plannerEvent: true,
      },
      {
        name: 'Planner/Documents',
        label: 'planner_documents',
        route: 'planner.documents',
        params: ['event.id'],
        plannerEvent: true,
      },
      {
        name: 'Planner/Reporting',
        label: 'planner_reporting',
        route: 'planner.reporting',
        params: ['event.id'],
        plannerEvent: true,
      },
    ],
    desktopOnly: true,
    selector: 'header_menu_planner',
  },
  {
    // Event content lives in its own group because more than one product consumes it. Organisation
    // & Event Sidebar (Arnold, 22 Oct 2026): „Bibliotheken" (the company's asset library and texts,
    // inside the event) above „Programm" (agenda, speakers, exhibitors, locations, tracks; served by
    // the eventApp.embed route, hence the EventApp/* child names). Libraries require their module
    // or the Marketplace; the parent opens the first available page.
    name: 'EventProgram',
    navKey: 'content',
    label: 'content',
    icon: ['fas', 'layer-group'],
    route: 'events.dam.index',
    params: ['event.id'],
    anyFeature: [
      'pwa-builder',
      'scan-my-lead',
      'marketplace',
      'dam',
      'content',
    ],
    hideWithoutChildren: true,
    dropdown: true,
    desktopOnly: true,
    isMobileChildMenuShown: false,
    child: [
      // SN-069 (D107): the event's media page is the library's own page (Dam/Index), as on master.
      // Without the booked library, an opted-in event holds only its own media and texts.
      {
        name: 'Dam/Index',
        label: 'event_media',
        route: 'events.dam.index',
        permission: 'dam:view',
        anyFeature: ['marketplace', 'dam'],
        group: 'libraries',
      },
      // SN-069: the event's texts page is the library's own page (Content/Index), as on master.
      {
        name: 'Content/Index',
        label: 'event_texts',
        route: 'events.content.texts.index',
        permission: 'content:text:list',
        anyFeature: ['marketplace', 'content'],
        group: 'libraries',
      },
      // The Agenda-Builder opens for the Event App or ScanMyLead (EventAppEmbedPage), not for
      // Airstage alone; the other Programm pages also open for Airstage.
      eventAppChild('AgendaBuilder', 'agenda_builder', 'agenda-builder', {
        ...PROGRAMME,
        anyFeature: ['pwa-builder', 'scan-my-lead'],
      }),
      eventAppChild(
        'Speakers',
        'agenda_speakers',
        'agenda-speakers',
        PROGRAMME,
      ),
      eventAppChild('ExhibitorsV2', 'exhibitors', 'exhibitors', PROGRAMME),
      // Locations and Tracks came back here in the 2026-08-18 alignment meeting,
      // which is the product decision the #5399 nav cleanup asked for: they are
      // master data shared by the Event App and Scan My Lead, and managing them
      // only inline in the Agenda Builder left them too hidden to find. Tags
      // stay inline - nothing outside the agenda consumes them.
      eventAppChild(
        'Locations',
        'agenda_locations',
        'event-locations',
        PROGRAMME,
      ),
      eventAppChild('Tracks', 'agenda_tracks', 'event-tracks', PROGRAMME),
    ],
    buttons: [],
    selector: 'header_menu_content',
  },
  {
    name: 'LandingPages/CenterstageEmbed',
    navKey: 'landingpage',
    label: 'landingpage',
    feature: 'landingpage',
    icon: ['far', 'browser'],
    route: 'landingPages.centerstage.embed',
    params: ['event.id'],
    activeRoutes: [],
    child: [],
    buttons: [],
    permission: 'centerstage:view',
    selector: 'header_menu_landingpage',
    desktopOnly: true,
  },
  {
    name: 'Airstage/Sites',
    label: 'airstage',
    icon: ['fas', 'grid-2-plus'],
    route: 'airstage.sites.page',
    params: ['event.id'],
    feature: 'airstage',
    permission: 'airstage:view',
    activeComponents: ['Airstage/Editor', 'Airstage/Forms', 'Airstage/Domains'],
    activeRoutes: [],
    child: [],
    buttons: [],
    selector: 'header_menu_airstage',
    desktopOnly: true,
  },
  {
    name: 'Events/CustomLandingpage',
    label: 'custom_landingpage',
    icon: ['far', 'browser'],
    route: 'events.customLandingpage',
    params: ['event.id'],
    activeRoutes: [],
    child: [],
    buttons: [],
    permission: 'customLandingpage:view',
    showForAirlstAdmin: true,
    newTab: true,
    selector: 'header_menu_custom_landingpage',
    desktopOnly: true,
  },
  {
    name: 'EventApp',
    navKey: 'event_app',
    label: 'event_app',
    icon: ['fas', 'mobile-screen-button'],
    route: 'eventApp.embed',
    params: ['event.id'],
    feature: 'pwa-builder',
    dropdown: true,
    desktopOnly: true,
    isMobileChildMenuShown: false,
    child: [
      eventAppChild('AppSettingsV2', 'app_settings', 'event-apps'),
      eventAppChild('Reporting', 'reporting', 'reporting', {
        permission: 'event:statistic:view',
      }),
      {
        name: 'EventApp/AppBuilder',
        label: 'app_builder',
        route: 'eventApp.builder',
      },
      eventAppChild('EventMap', 'event_map', 'event-map-editor'),
      eventAppChild(
        'PushNotifications',
        'push_notifications',
        'push-notifications',
        { feature: 'event-app-push', premium: true },
      ),
      eventAppChild('Newsfeed', 'newsfeed', 'newsfeed', {
        feature: 'event-app-newsfeed',
        premium: true,
      }),
      eventAppChild('Faqs', 'faqs', 'faqs'),
      eventAppChild('Surveys', 'surveys', 'surveys', {
        feature: 'event-app-survey',
        premium: true,
      }),
      eventAppChild('Galleries', 'galleries', 'galleries', {
        feature: 'event-app-media-gallery',
        premium: true,
      }),
      // No media library here: the event media library page is retired, its
      // files moved into the company asset library
      // (event-app:migrate-assets-to-dam), and an event reaches that library
      // through Content > Media (SN-069). One link per place.
      eventAppChild('Directories', 'directories', 'directories'),
      eventAppChild('Channels', 'chat_channels', 'channels', {
        feature: 'event-app-chat',
        premium: true,
      }),
      eventAppChild('Quizzes', 'quizzes', 'quizzes', {
        feature: 'event-app-quiz',
        premium: true,
      }),
      eventAppChild('Logs', 'logs', 'event-app-logs'),
    ],
    buttons: [],
    selector: 'header_menu_event_app',
  },
  {
    // Connect & Meet. Its own group rather than a child of Event App: meeting
    // settings, areas and tables hang off the EVENT, so a fair with no PWA
    // configures them all the same. The `event-app-meetings` module is
    // default-off, so the group is invisible until somebody switches it on.
    name: 'EventApp/Meetings',
    label: 'meetings',
    // `people-arrows`: two people facing each other, which is what a meeting
    // is. It is registered under `fas` in Libraries/fontawesome.js, and that
    // matters more than the glyph choice does - FontAwesome answers a
    // prefix/name pair it does not hold with nothing at all and logs nothing,
    // so this group rendered NO icon while it asked for
    // `['fas', 'arrow-right-arrow-left']`, which only exists under `fal`.
    // Menu.spec.js asserts the pair against the real library.
    icon: ['fas', 'people-arrows'],
    route: 'eventApp.embed',
    params: ['event.id'],
    feature: 'event-app-meetings',
    dropdown: true,
    desktopOnly: true,
    isMobileChildMenuShown: false,
    child: [
      // Plan order: the worklist first, then the screens that configure it.
      eventAppChild('Meetings', 'meeting_requests', 'meeting-requests'),
      eventAppChild('Meetings', 'meeting_area', 'meeting-area'),
      eventAppChild('Meetings', 'meeting_slot_rules', 'meeting-slot-rules'),
      eventAppChild('Meetings', 'meeting_matchmaking', 'meeting-matchmaking'),
      eventAppChild(
        'Meetings',
        'meeting_session_registration',
        'meeting-session-registration',
      ),
      // Last: the read-only report over everything the screens above produce.
      eventAppChild('Meetings', 'meeting_statistics', 'meeting-statistics'),
    ],
    buttons: [],
    selector: 'header_menu_meetings',
  },
  {
    name: 'ScanMyLead',
    navKey: 'scan_my_lead',
    label: 'scan_my_lead',
    icon: ['fas', 'qrcode'],
    route: 'scanMyLead.screen',
    params: ['event.id'],
    feature: 'scan-my-lead',
    scanMyLeadStart: true,
    dropdown: true,
    desktopOnly: true,
    isMobileChildMenuShown: false,
    child: [
      scanMyLeadChild('Dashboard', 'dashboard', 'dashboard'),
      scanMyLeadChild('Leads', 'sml_leads', 'leads'),
      scanMyLeadChild(
        'AccountManagers',
        'sml_account_managers',
        'account-managers',
      ),
      scanMyLeadChild('Program', 'sml_program', 'program'),
      scanMyLeadChild('Settings', 'settings', 'settings'),
      scanMyLeadChild('Forms', 'lead_forms', 'lead-forms'),
      scanMyLeadChild('Roles', 'sml_pwa_roles', 'roles'),
      scanMyLeadChild('Reports', 'sml_reports', 'reports'),
      scanMyLeadChild('Exports', 'sml_exports', 'exports'),
      scanMyLeadChild('Audit', 'sml_audit', 'audit'),
      {
        name: 'ScanMyLead/Start',
        label: 'sml_activation',
        route: 'scanMyLead.start',
        extraParams: { activated: 1 },
        scanMyLeadManage: true,
      },
    ],
    buttons: [],
    selector: 'header_menu_scan_my_lead',
  },
  {
    name: 'Tickets/List',
    label: 'tickets_and_addons',
    navKey: 'tickets',
    icon: ['fas', 'ticket-simple'],
    route: 'bookable.tickets.list',
    params: ['event.id'],
    dropdown: true,
    // Every ticket and add-on route sits in the bookables group behind
    // BookableObjectsFeature, so the entry needs that module (Marketplace `ticket`).
    feature: 'bookable-objects',
    permission: 'bookableObject:ticket:list',
    child: [
      {
        name: 'TicketBookables/GettingStarted',
        label: 'getting_started',
        icon: ['far', 'sparkles'],
        route: 'bookable.getting-started',
        permission: 'bookableObject:ticket:list',
        showIcon: true,
      },
      {
        name: 'TicketBookables/List',
        label: 'menu_tickets',
        icon: ['fal', 'file-pdf'],
        route: 'bookable.tickets.list',
        feature: 'ticket_bookable_objects',
        permission: 'bookableObject:ticket:list',
      },
      {
        name: 'Tickets/List',
        label: 'menu_ticket_design',
        icon: ['fal', 'file-pdf'],
        route: 'tickets.index',
        feature: 'ticket_bookable_objects',
        permission: 'ticket:view',
      },
      {
        name: 'AddOns/List',
        label: 'bookable_groups',
        icon: ['fas', 'cube'],
        route: 'bookable.list',
        feature: 'bookable-objects',
        permission: 'bookableObject:group:list',
        selector: 'header_menu_bookablegroups',
      },
    ],
    selector: 'header_menu_tickets',
  },
  {
    name: 'Payments',
    navKey: 'payments',
    label: 'payments',
    icon: ['fas', 'credit-card'],
    route: 'events.settings.payments.show',
    params: ['event.id'],
    dropdown: true,
    feature: 'event_payments',
    permission: 'event:setting:payments:view',
    selector: 'header_menu_payments',
    child: [
      {
        name: 'Payments/GettingStarted',
        label: 'getting_started',
        icon: ['far', 'sparkles'],
        route: 'events.settings.payments.getting-started.show',
        permission: 'event:setting:payments:view',
        showIcon: true,
      },
      {
        name: 'Events/Settings/Invoicing',
        label: 'invoicing',
        route: 'events.settings.invoicing.show',
        permission: 'event:setting:payments:view',
      },
      {
        name: 'Events/Settings/Legal',
        label: 'legal',
        route: 'events.settings.legal.show',
        permission: 'event:setting:payments:view',
      },
      {
        name: 'Events/Settings/Payments',
        label: 'payment_provider',
        route: 'events.settings.payments.show',
        permission: 'event:setting:payments:view',
      },
      {
        name: 'PromoCode/List',
        label: 'promo_codes',
        icon: ['fal', 'tag'],
        route: 'promo-codes.index',
        permission: 'promoCode:list',
      },
      {
        name: 'Payments/OrderList',
        label: 'orders',
        route: 'payment.order.list',
        permission: 'payment:order:list',
      },
    ],
  },
  {
    name: 'Emails/Templates/List',
    label: 'email',
    icon: ['fas', 'envelope-circle-check'],
    route: 'emails.templates.index',
    params: ['event.id'],
    dropdown: true,
    activeRoutes: [],
    permission: 'email:template:list',
    desktopOnly: true,
    isMobileChildMenuShown: false,
    childSubmenu: false,
    child: [
      {
        name: 'Emails/Templates/List',
        label: 'email_templates',
        icon: ['fal', 'browser'],
        route: 'emails.templates.index',
        permission: 'email:template:list',
      },
      {
        name: 'Emails/SenderIdentity/List',
        label: 'email_senders',
        icon: ['fal', 'envelope-open-text'],
        route: 'events.settings.senderIdentities.index',
        permission: 'email:senderIdentity:list',
      },
      {
        name: 'Emails/Settings/CustomMailers/List',
        label: 'email_senders_custom_smtp',
        icon: ['fal', 'server'],
        route: 'emails.settings.customMailers.index',
        permission: 'email:customMailer:list',
        module: 'custom-mailer',
      },
      {
        name: 'Emails/Statistic/Index',
        label: 'email_statistics',
        icon: ['fal', 'chart-pie-alt'],
        route: 'emails.statistics',
        permission: 'email:statistic:view',
      },
      {
        name: 'Emails/Sent/List',
        label: 'sent_emails',
        icon: ['fal', 'envelope-open-text'],
        route: 'emails.sent.list',
        permission: 'email:sent:list',
      },
      {
        name: 'Emails/EmailSubscription/List',
        label: 'opt_in_history',
        route: 'emails.subscription.list',
        permission: 'contact:emailSubscription:list',
      },
      {
        name: 'Emails/MarketingConsent/List',
        label: 'marketing_opt_in_history',
        route: 'emails.marketing-consents.list',
        permission: 'contact:emailSubscription:list',
      },
    ],
    buttons: [],
    selector: 'header_menu_emails',
  },
  {
    name: 'Sms/Sent/List',
    label: 'sms',
    navKey: 'sms',
    feature: 'sms',
    route: 'sms.sent.list',
    icon: ['fas', 'message'],
    params: ['event.id'],
    dropdown: true,
    activeRoutes: [],
    permission: 'sms:sent:list',
    check_sms_enabled: true,
    desktopOnly: true,
    isMobileChildMenuShown: false,
    childSubmenu: false,
    child: [
      {
        name: 'Sms/Templates/List',
        label: 'sms_template',
        route: 'sms.templates.list',
        permission: 'sms:sent:list',
      },
      {
        name: 'Sms/Sent/List',
        label: 'sent_sms',
        route: 'sms.sent.list',
        permission: 'sms:sent:list',
      },
      {
        name: 'Sms/Statistics/Index',
        label: 'sms_statistics',
        route: 'sms.statistics',
        permission: 'sms:statistic:view',
      },
      {
        name: 'Sms/SenderIdentities/List',
        label: 'sms_sender_identities',
        route: 'events.settings.smsSenderIdentities.index',
        permission: 'sms:senderIdentity:list',
      },
      {
        name: 'Sms/Pricing/List',
        label: 'sms_pricing',
        route: 'sms.pricing.list',
        permission: 'sms:sent:list',
      },
    ],
    buttons: [],
    selector: 'header_menu_emails',
  },
  {
    name: 'Documents/List',
    navKey: 'documents',
    label: 'event_documents',
    icon: ['fas', 'file-lines'],
    route: 'documents.index',
    permission: 'document:list',
    feature: 'documents',
    params: ['event.id'],
    dropdown: false,
    desktopOnly: true,
    selector: 'header_menu_documents',
  },
  {
    name: 'TableSeating',
    navKey: 'seating',
    label: 'seating',
    anyFeature: ['seating', 'seating-native'],
    icon: ['fas', 'loveseat'],
    route: 'seating.rooms.index',
    params: ['event.id'],
    dropdown: true,
    activeRoutes: [],
    child: [
      {
        name: 'Seating/Rooms',
        label: 'tableseating_manage',
        icon: ['fal', 'loveseat'],
        route: 'seating.rooms.index',
        feature: 'seating-native',
        permission: 'tableSeating:view',
      },
      {
        name: 'TableSeating/TableSeatingEmbed',
        label: 'tableseating_manage_legacy',
        icon: ['fal', 'file-pdf'],
        route: 'tableSeating.embed',
        feature: 'seating',
        permission: 'tableSeating:view',
      },
      {
        name: 'Bookings/Seats/List',
        label: 'tableseating_table',
        icon: ['fal', 'file-pdf'],
        route: 'bookings.seats.list',
        permission: 'guest:seat:list',
      },
    ],
    buttons: [],
    permission: 'tableSeating:view',
    selector: 'header_menu_seating',
    desktopOnly: true,
    isMobileChildMenuShown: false,
  },
  {
    name: 'Events/Settings/Settings',
    label: 'settings',
    icon: ['fas', 'gear'],
    route: 'events.settings.show',
    params: ['event.id'],
    dropdown: true,
    isMobileChildMenuShown: false,
    activeRoutes: [],
    permission: 'event:setting:view',
    child: [
      {
        name: 'Events/Settings/Settings',
        label: 'event_information',
        icon: ['fal', 'chart-pie-alt'],
        route: 'events.settings.show',
        permission: 'event:setting:view',
      },
      {
        name: 'Events/Settings/Field',
        label: 'field_settings',
        icon: ['fal', 'chart-pie-alt'],
        route: 'events.settings.field.show',
        permission: 'event:setting:view',
      },
      {
        name: 'Events/Settings/Advanced',
        label: 'event_settings',
        icon: ['fal', 'cogs'],
        route: 'events.settings.advanced.show',
        permission: 'event:setting:advanced:view',
      },
      {
        name: 'Events/Limits/List',
        label: 'limits',
        icon: ['fal', 'exclamation-triangle'],
        route: 'events.settings.fieldLimits.index',
        permission: 'extendedField:limit:list',
      },
      {
        // Customer navigation preferences (Marketplace concept §7): which entries this event shows.
        name: 'Events/Settings/Navigation',
        label: 'navigation',
        icon: ['fal', 'bars'],
        route: 'events.settings.navigation.show',
        permission: 'event:setting:view',
        feature: 'marketplace',
      },
      {
        // The component Inertia renders, not a path guessed from the route:
        // the section a page belongs to is found by matching this against
        // `usePage().component` (sectionTabs.js).
        name: 'Workflows/List',
        label: 'workflows',
        icon: ['fal', 'arrow-up'],
        route: 'workflows.list',
        permission: 'workflow:view',
      },
    ],
    buttons: [],
    selector: 'header_menu_settings',
  },
]

export const getMenu = (menu) => {
  return useMenus.find(({ name }) => menu.toLowerCase().includes(name))
}

export const getPage = (route) => {
  return useMenus.find(({ name }) => route.toLowerCase().includes(name))?.label
}

export const useHelpCenterUrl = () => {
  return 'https://help.airlst.app/'
}

// The event-menu routes a Guest-User may open (AIRLST-2527). Mirrors the
// backend allowlist in RestrictGuestUserRoutesMiddleware: everything else
// answers 403 for them, so it is hidden rather than shown and refused.
export const GUEST_USER_MENU_ROUTES = ['bookings.index']

/**
 * Whether the current user may see a menu entry: its permission (or AirLST staff for
 * `showForAirlstAdmin`), its feature gates and its constant flag. Shared by the event menu and the
 * organisation sections (sectionTabs.js). A Guest-User sees only GUEST_USER_MENU_ROUTES.
 *
 * @param {object} item
 * @returns {boolean}
 */
export const passesMenuGates = (item) => {
  if (
    usePage().props._user?.is_guest_user === true &&
    !GUEST_USER_MENU_ROUTES.includes(item.route)
  ) {
    return false
  }

  const hasPermission = item.permission ? usePermission(item.permission) : true

  // AirLST-internal tools (e.g. Custom Landingpage) are visible to AirLST
  // staff regardless of company permission; everyone else falls back to the
  // item's permission gate.
  const isAirlstAdmin = usePage().props._user?.airlst_admin === true
  const permitted =
    item.showForAirlstAdmin && isAirlstAdmin ? true : hasPermission

  let featureEnabled = true
  if (item.anyFeature?.length) {
    featureEnabled = item.anyFeature.some((feature) => useFeature(feature))
  } else if (item.feature?.length) {
    featureEnabled = useFeature(item.feature)
  }

  if (featureEnabled && item.hideForAnyFeature?.length) {
    featureEnabled = !item.hideForAnyFeature.some((feature) =>
      useFeature(feature),
    )
  }

  if (!featureEnabled && item.scanMyLeadStart === true) {
    featureEnabled = usePage().props._scanMyLead?.start === true
  }

  if (item.scanMyLeadManage === true) {
    featureEnabled = usePage().props._scanMyLead?.manageActivation === true
  }

  const constantEnabled = item.constantFlag
    ? usePage().props._constants?.[item.constantFlag] === true
    : true

  const plannerEvent = usePage().props._planner?.event
  const plannerEnabled = item.plannerEvent
    ? plannerEvent?.canView === true ||
      (item.plannerStart === true &&
        (plannerEvent?.canStart === true || plannerEvent?.hidden === true))
    : true

  return permitted && featureEnabled && constantEnabled && plannerEnabled
}

export const useActiveMenuElements = () => {
  const currentComponent = usePage().component

  return useMenus
    .filter(passesMenuGates)
    .map((menu) => {
      const children = (menu.child ?? []).filter(passesMenuGates)
      const hasActiveChild = children.some(
        (child) => child.name === currentComponent,
      )

      // Dry run A-01: a dropdown whose children are all hidden (Planung while the trial can still
      // be started or the planner is hidden) would open empty. Without children the entry is a
      // plain link to its own page, in both sidebars.
      const emptyDropdown =
        menu.dropdown === true && children.length === 0 && Boolean(menu.route)

      return {
        ...menu,
        dropdown: emptyDropdown ? false : menu.dropdown,
        route:
          menu.name === 'TableSeating'
            ? (children[0]?.route ?? menu.route)
            : menu.name === 'EventProgram'
              ? (children[0]?.route ?? menu.route)
              : menu.route,
        child: children,
        childCollapse: hasActiveChild || menu.childCollapse || false,
      }
    })
    .filter(
      (menu) =>
        (menu.hideWithoutChildren !== true && menu.hideWhenEmpty !== true) ||
        menu.child.length > 0,
    )
}
